import { MarketTypes, SubmissionAction, SubmissionState } from "@qw/graphql";
import { DropdownMenuItem, DropdownMenuSeparator, Icon, type MaterialSymbol, MoreMenu } from "@qw/ui";
import type { ReactNode } from "react";
import { PRODUCTION_APP_ORIGINS } from "@/constants/app-origins";
import { canRepairSubmission } from "./submission-repair";
import { type RailSubmission, useSubmissionActions } from "./use-submission-actions";

// The rail is backed by the shared Opportunities query, not web's OpportunityDetails.
const REFETCH_QUERIES = ["Opportunities"];

const actionItem = (key: string, icon: MaterialSymbol, label: string, onClick: () => void) => (
  <DropdownMenuItem key={key} onClick={onClick}>
    <Icon icon={icon} />
    {label}
  </DropdownMenuItem>
);

const linkItem = (key: string, icon: MaterialSymbol, label: string, href: string) => (
  <DropdownMenuItem key={key} asChild>
    <a href={href} target="_blank" rel="noreferrer">
      <Icon icon={icon} />
      {label}
    </a>
  </DropdownMenuItem>
);

export const SubmissionMenu = ({ insuredId, submission }: { insuredId: string; submission: RailSubmission }) => {
  const { handleAddRuleOutReason, handleConfirmation, handleRepair, handleTransition } =
    useSubmissionActions(REFETCH_QUERIES);

  const { isApiSubmission, marketType, portalLink } = submission.appetiteProduct;

  const ruleOut = actionItem("rule-out", "block", "Rule Out", () =>
    handleTransition(submission, SubmissionAction.RuleOut)
  );
  const revert = actionItem("revert", "undo", "Revert", () => handleTransition(submission, SubmissionAction.Revert));
  const repair = actionItem("repair", "build", "Repair", () => handleRepair(submission));

  const stateActions = (): ReactNode[] => {
    switch (submission.state) {
      case SubmissionState.Pending:
        return [ruleOut];
      case SubmissionState.ReadyToSubmit:
        // An API market prices through Vave, and web withholds every generic action here so a
        // broker cannot skip that dialog: Mark Submitted would move the row out of
        // ReadyToSubmit, which is the only state Vave pricing can start from, with no way back.
        if (isApiSubmission) {
          return [];
        }
        return [
          // Submitting stays a routed page in web; the rail links out to it rather than porting it.
          linkItem(
            "submit-to-carrier",
            "mail",
            "Submit to Carrier",
            `${PRODUCTION_APP_ORIGINS.brokerage}/insured/${insuredId}/submissions/${submission.id}/submit-to-carrier`
          ),
          actionItem("mark-submitted", "check_circle", "Mark Submitted", () =>
            handleTransition(submission, SubmissionAction.Submit)
          ),
          ruleOut,
        ];
      case SubmissionState.Submitted:
        return [
          actionItem("blocked", "front_hand", "Blocked", () =>
            handleTransition(submission, SubmissionAction.BlockMarket)
          ),
          ...(marketType === MarketTypes.BindingAuthority
            ? [
                actionItem("referred", "forward", "Mark as Referred", () =>
                  handleTransition(submission, SubmissionAction.Refer)
                ),
              ]
            : []),
          actionItem("declined", "thumb_down", "Declined", () =>
            handleConfirmation(submission, SubmissionAction.Decline)
          ),
          <DropdownMenuSeparator key="rule-out-separator" />,
          ruleOut,
        ];
      case SubmissionState.Declined:
      case SubmissionState.MarketBlocked:
        return [canRepairSubmission(submission) ? repair : revert];
      case SubmissionState.RuledOut:
        // Guard before reading the last transition: a submission with no logs has no rule
        // out transition to hang a reason off.
        if (canRepairSubmission(submission)) {
          return [repair];
        }
        return [
          ...(submission.stateTransitions.at(-1)?.details
            ? []
            : [
                actionItem("add-rule-out-reason", "edit_note", "Add rule out reason", () =>
                  handleAddRuleOutReason(submission)
                ),
              ]),
          revert,
        ];
      default:
        return [];
    }
  };

  const carrierPortal = portalLink ? linkItem("carrier-portal", "language", "Carrier Portal", portalLink) : null;
  const actions = stateActions();

  if (!carrierPortal && actions.length === 0) {
    return null;
  }

  return (
    <MoreMenu tooltip="Market actions">
      {carrierPortal}
      {carrierPortal && actions.length > 0 && <DropdownMenuSeparator />}
      {actions}
    </MoreMenu>
  );
};
